import React from 'react';
import { startCase } from 'lodash';
import { WidgetType } from 'components/DashboardWidget/types';
import { Cell, Link } from '../components';
import { Formatter } from '../utils';

function getColumns() {
  return {
    year: { sorter: true },
    month: { sorter: true },
    day: { sorter: true },
    hour: { sorter: true },

    campaignId: {
      title: 'Campaign',
      width: 200,
      render: ({ name, id }: { name: string; id: string }) => (
        <Cell id={id} title={<Link to={`/campaigns/edit/${id}`}>{name}</Link>} />
      ),
    },
    creativeId: {
      title: 'Creative',
      width: 200,
      render: ({ name, id }: { name: string; id: string }) => <Cell id={id} title={name} />,
    },
    creativeType: {
      title: 'Creative Type',
      width: 200,
    },
    exchange: {
      title: 'Exchange',
      width: 200,
    },
    publisherId: {
      title: 'Publisher',
      width: 200,
    },
    publisherApp: {
      title: 'App',
      width: 200,
    },
    publisherAppBundle: {
      title: 'App Bundle',
      width: 200,
    },
    deviceOs: {
      title: 'Device OS',
      width: 200,
    },

    bids: { sorter: false, render: Formatter.count },
    wins: { sorter: true, render: Formatter.count },
    win_rate: {
      title: 'Win Rate',
      render: (val: number | '-') => `${Formatter.count(val, 4)}%`,
      sorter: false,
    },
    total_impressions: { title: 'Total Impressions', sorter: true, render: Formatter.count },
    impressions: { sorter: true, render: Formatter.count },
    af_impressions: { title: 'AF Impressions', sorter: false, render: Formatter.count },
    viewable_impression:{ title: 'Viewable Impressions', sorter: true, render: Formatter.count },
    total_clicks: { title: 'Total Clicks', sorter: true, render: Formatter.count },
    clicks: { sorter: true, render: Formatter.count },
    af_clicks: { title: 'AF Clicks', sorter: false, render: Formatter.count },
    installs: { sorter: true, render: Formatter.count },
    af_installs: { title: 'AF Installs', sorter: false, render: Formatter.count },
    conversions: { sorter: true, render: Formatter.count },
    CTR: { sorter: true, render: (val: number | '-') => `${Formatter.count(val, 4)}%` },
    CT_CR: { title: 'CT CR', sorter: true,  render: (val: number | '-') => `${Formatter.count(val, 4)}%` },
    IPM: { sorter: true, render: Formatter.count },
    eCPM: {sorter: true, title: 'eCPM', render: (val: number | '-') => `$${Formatter.count(val, 4)}` },
    eCPC: { sorter: true, title: 'eCPC', render: (val: number | '-') => `$${Formatter.count(val, 4)}` },
    eCPI: {
      title: 'eCPI',
      sorter: true,
      render: (val: number | '-') => (val === 0 || !val ? '-' : `$${Formatter.count(val, 4)}`),
    },
    eCPA: {
      title: 'eCPA',
      sorter: true,
      render: (val: number | '-') => (val === 0 || !val ? '-' : `$${Formatter.count(val, 4)}`),
    },
    spend: { sorter: true, render: (val: number | '-') => `$${Formatter.count(val, 4)}` },
    win_spend: {
      title: 'Win Spend',
      sorter: true,
      render: (val: number | '-') => `$${Formatter.count(val, 4)}`,
    },
    start: { sorter: false, render: Formatter.count },
    firstquartile: { title: 'First Quartile', sorter: false, render: Formatter.count },
    midpoint: { sorter: false, render: Formatter.count },
    thirdquartile: { title: 'Third Quartile', sorter: false, render: Formatter.count },
    complete: { sorter: false, render: Formatter.count },
  };
}

function getColumnsArr() {
  const columns = getColumns();
  return Object.entries(columns).map(([field, options]) => ({
    title: startCase(field),
    dataIndex: field,
    ...options,
  }));
}

const filterColumns = (columns: string[]) =>
  getColumnsArr().filter((item) => columns.indexOf(item.dataIndex) !== -1);

/**
 * App event names belong to an advertiser, so they cannot be part of the
 * static metric registry above. Keep the API's ordering where it supplies the
 * selected events, and retain selected events absent from the metadata so the
 * user's explicit column choice is still visible.
 */
function getEventColumns(responseColumns: string[] = [], selectedEvents: string[] = []) {
  const validEvents = Array.from(
    new Set(selectedEvents.filter((event): event is string => typeof event === 'string' && !!event)),
  );
  const selectedSet = new Set(validEvents);
  const orderedEvents = [
    ...responseColumns.filter((column) => selectedSet.has(column)),
    ...validEvents.filter((event) => !responseColumns.includes(event)),
  ];
  const staticColumns = getColumns();

  return orderedEvents
    .filter((event, index) => orderedEvents.indexOf(event) === index)
    .filter((event) => !Object.prototype.hasOwnProperty.call(staticColumns, event))
    .map((event) => ({
      title: startCase(event),
      dataIndex: event,
      render: Formatter.count,
    }));
}

const commonTimeGroups = ['year', 'month', 'day', 'hour'];

const commonGroups = [
  'campaignId',
  'exchange',
  'publisherApp',
  'publisherAppBundle',
  'publisherId',
  'creativeId',
  'creativeType',
  'deviceOs',
];

export const BID_COUNTS_SUPPORTED_GROUPS = [
  'advertiserId',
  'orderId',
  'campaignId',
  'creativeId',
  'publisherAppBundle',
  'year',
  'month',
  'day',
];

const commonSums = [
  'impressions',
  'viewable_impression',
  'clicks',
  'win_rate',
  'installs',
  'conversions',
  'CTR',
  'CT_CR',
  'IPM',
  'spend',
  'win_spend',
  'eCPM',
  'eCPC',
  'eCPI',
  'eCPA',
];

export type StatsType = 'custom' | 'campaign' | 'creative' | 'dashboard';

function getSumsAndGroups(statsType: StatsType, widgetType?: WidgetType) {
  let sums: string[] = [];
  let groups: string[] = [];
  if (statsType === 'custom') {
    sums = commonSums;
    groups = [...commonTimeGroups, ...commonGroups];
  }
  else if (statsType === 'campaign') {
    sums = [
      ...commonSums,
      'af_impressions',
      'af_clicks',
      'af_installs',
      'total_impressions',
      'viewable_impression',
      'total_clicks',
      'wins',
      'bids',
      'start',
      'firstquartile',
      'midpoint',
      'thirdquartile',
      'complete',
    ];
    groups = ['creativeType', 'targetBid', 'dailyBudget', 'totalBudget', 'bidMultiplier'];
  }
  else if (statsType === 'creative') {
    sums = [...commonSums, 'wins', 'bids', 'start', 'firstquartile', 'midpoint', 'thirdquartile', 'complete'];
    groups = ['type', 'adFormat', 'orientation', 'tracker', 'preview', 'resolution', 'schedule'];
  }
  else if (statsType === 'dashboard') {
    sums = ['clicks', 'impressions', 'installs', 'conversions'];
    if (widgetType === 'LINE_GRAPH' || widgetType === 'MIXED_GRAPH') {
      groups = [...commonTimeGroups, 'year:month:day', 'year:month'];
    }
    else if (widgetType === 'BAR_GRAPH') {
      groups = [...commonTimeGroups, 'year:month:day', 'year:month', ...commonGroups];
    }
    else if (widgetType === 'PIE_CHART') {
      groups = commonGroups;
    }
    else {
      groups = [...commonTimeGroups, ...commonGroups];
    }
  }

  return {
    sums: Formatter.makeOptions(sums, true),
    groups: Formatter.makeOptions(groups, true),
  };
}

function getCustomLabel(arr: any[] = []) {
  return arr.map((item) => {
    switch (item.label) {
      case 'Campaign Id': {
        return { ...item, label: 'Campaign' };
      }
      case 'Advertiser Id': {
        return { ...item, label: 'Advertiser' };
      }
      case 'Publisher Id': {
        return { ...item, label: 'Publisher' };
      }
      case 'Creative Id': {
        return { ...item, label: 'Creative' };
      }
      case 'Cpm': {
        return { ...item, label: 'CPM' };
      }
      case 'Cpc': {
        return { ...item, label: 'CPC' };
      }
      case 'Cpi': {
        return { ...item, label: 'CPI' };
      }
      case 'Cpa': {
        return { ...item, label: 'CPA' };
      }
      case 'E Cpm': {
        return { ...item, label: 'eCPM' };
      }
      case 'E Cpc': {
        return { ...item, label: 'eCPC' };
      }
      case 'E Cpi': {
        return { ...item, label: 'eCPI' };
      }
      case 'E Cpa': {
        return { ...item, label: 'eCPA' };
      }
      case 'Ctr': {
        return { ...item, label: 'CTR' };
      }
      case 'Ct Cr': {
        return { ...item, label: 'CT CR' };
      }
      case 'Ipm': {
        return { ...item, label: 'IPM' };
      }
      case 'Af Impressions': {
        return { ...item, label: 'AF Impressions' };
      }
      case 'Af Clicks': {
        return { ...item, label: 'AF Clicks' };
      }
      case 'Af Installs': {
        return { ...item, label: 'AF Installs' };
      }
      default:
        return item;
    }
  });
}

export { getColumns, getColumnsArr, filterColumns, getEventColumns, getSumsAndGroups, getCustomLabel };
